import { useForm, useStore } from "@tanstack/react-form";
import { Textarea } from "@/components/ui/textarea";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import {
  productFormSchema,
  type ProductFormData,
} from "../../../shared/schemas/product.schema";
import { buildClonedProductFormData } from "../../../shared/schemas/product-clone.helper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

import { cn } from "@/lib/utils";
import { focusFirstError, collectFormErrorMessages } from "../../../lib/form/formUtils";
import {
  Save,
  Loader2,
  Upload,
  X,
  Calculator,
  Settings,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  normalizePaperDimension,
  isSuspiciouslySmallDimension,
} from "@/lib/paper-format.helper";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useParams } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  TanstackFieldWrapper,
  TanstackFormControl,
} from "@/components/ui/tanstack-form";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { useEntityQuickAdd } from '@/components/shared/useEntityQuickAdd';
import { ComboboxConfig } from "@/components/shared/ComboboxConfig";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import { EditableCombobox } from "@/components/shared/EditableCombobox";
import { NumericInput } from "@/components/shared/NumericInput";
import { PaperSizeInput } from "@/components/shared/PaperSizeInput";
import { ImageDropzone } from "@/components/shared/ImageDropzone";
import {
  useCustomerLookup,
  useMaterialLookup,
  useMaterialTypeLookup,
  useProductTypeLookup,
  useProcessLookup,
  useUnitLookup,
  useProductLookup,
  ensureFKOption,
} from "@/hooks/useFKLookup";
import { trpcClient } from "@/lib/trpc";
import { uploadApi } from "@/lib/api-client";
import { ProcessCreateModal } from "../../system/processes/ProcessCreateModal";
import MaterialTypeFormModal from "../material-types/MaterialTypeFormModal";
import { ProductComponentsList } from "./components/ProductComponentsList";
import { ProductProcessConfigsList } from "./components/ProductProcessConfigsList";
import { ProcessRoutingPicker } from "./components/ProcessRoutingPicker";
import { sortPackagingProcessIds } from "../../../shared/helpers/packaging-routing-sorter";
import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import { useFormBlocker } from "@/hooks/useFormBlocker";
import { useConfigSuggestions } from "@/hooks/useConfigSuggestions";
import CustomerFormModal from "../customers/CustomerFormModal";
import MaterialFormModal from "../materials/MaterialFormModal";
import {
  GRAIN_DIRECTION_OPTIONS,
  FSC_CERTIFICATION_OPTIONS,
} from "@apv-erp/status";

interface ProductFormProps {
  defaultValues?: Partial<ProductFormData> & {
    processIds?: string[];
    currentVersion?: number;
  };
  onSubmit: (
    data: ProductFormData & { processIds?: string[] },
  ) => Promise<void | boolean>;
  onCancel: () => void;
  isSubmitting?: boolean;
  submitLabel?: string;
  enableBlocker?: boolean;
  lockedFields?: string[];
  isModal?: boolean;
  breadcrumb?: string[];
}

/**
 * Helper to determine the process group based on DB group, stepId and name keywords
 */
export function getProcessGroupKey(o: any): string {
  const stepId = (o.stepId || o.original?.stepId || "").toUpperCase().replace(/-/g, "_");
  // Mã tự sinh dùng gạch ngang (SAU-IN-THUC-NOI): chuẩn hoá để không bị nhận nhầm là công đoạn In (FB-7H1WJ3)
  const name = (o.label || o.name || "").toLowerCase();
  const dbGroup = (o.group || o.original?.group || "").toUpperCase();

  // 1. Direct stepId markers
  if (stepId.includes("TRUOC_IN") || stepId.includes("PRE_PRESS"))
    return "TRUOC_IN";
  if (stepId.includes("SAU_IN") || stepId.includes("POST_PRESS"))
    return "SAU_IN";
  if (stepId.includes("THANH_PHAM") || stepId.includes("TP"))
    return "THANH_PHAM";
  if (
    stepId.includes("IN") &&
    !stepId.includes("SAU_IN") &&
    !stepId.includes("TRUOC_IN")
  )
    return "IN";

  // 2. Mapping based on DB group
  if (["OFFSET_PRINTER", "FLEXO_PRINTER", "IN", "PRINTING"].includes(dbGroup))
    return "IN";
  if (
    [
      "COATER",
      "SILK_SCREEN",
      "HOT_FOIL",
      "SAU_IN",
      "POST_PRESS",
      "GIA_CONG_SAU_IN",
    ].includes(dbGroup)
  )
    return "SAU_IN";
  if (
    [
      "DIE_CUTTER",
      "FOLDER_GLUER",
      "WINDOW_PATCHER",
      "PACKING",
      "THANH_PHAM",
    ].includes(dbGroup)
  )
    return "THANH_PHAM";
  if (["TRUOC_IN", "PRE_PRESS"].includes(dbGroup)) return "TRUOC_IN";

  // Special DB groups that overlap (LAMINATOR, PAPER_CUTTER)
  if (dbGroup === "LAMINATOR") {
    if (name.includes("ghép")) return "TRUOC_IN";
    return "SAU_IN"; // Cán màng bóng/mờ/nhung...
  }
  if (dbGroup === "PAPER_CUTTER") {
    if (name.includes("xén") || name.includes("cắt giấy")) return "TRUOC_IN";
    return "THANH_PHAM"; // Cắt thành phẩm...
  }

  // 3. Keyword fallback for group: null or legacy groups
  if (
    name.includes("in offset") ||
    name.includes("in flexo") ||
    name.includes("in lụa") ||
    name.includes("cmyk") ||
    name.startsWith("in ") ||
    name.includes(" màu ")
  ) {
    if (name.includes("uv") || name.includes("phủ")) return "SAU_IN";
    return "IN";
  }
  if (
    name.includes("ghép") ||
    name.includes("metalize") ||
    name.includes("xén") ||
    name.includes("cắt giấy") ||
    name.includes("bát")
  ) {
    return "TRUOC_IN";
  }
  if (
    name.includes("cán") ||
    name.includes("phủ") ||
    name.includes("vecni") ||
    name.includes("uv") ||
    name.includes("ép nhũ") ||
    name.includes("ép kim") ||
    name.includes("nhũ") ||
    name.includes("kim") ||
    name.includes("tráng") ||
    name.includes("vân")
  ) {
    return "SAU_IN";
  }

  return "THANH_PHAM";
}

export default function ProductForm(props: ProductFormProps) {
  const processQuickAdd = useEntityQuickAdd("process"); // thêm mới bản ghi cha theo quyền (03/11/2026)
  const {
    defaultValues,
    onSubmit,
    onCancel,
    isSubmitting = false,
    submitLabel = "Lưu sản phẩm",
    enableBlocker = true,
    lockedFields,
    isModal = false,
    breadcrumb,
  } = props;

  const baseBreadcrumb = useMemo(() => {
    if (breadcrumb && breadcrumb.length > 0) {
      return breadcrumb;
    }
    return ["Sản phẩm"];
  }, [breadcrumb]);
  const { productId } = (useParams({ strict: false }) as any) || {};

  // Process multi-tag
  const [selectedProcessIds, _setSelectedProcessIds] = useState<string[]>(
    defaultValues?.processIds ?? [],
  );
  const processIdsRef = useRef(selectedProcessIds);
  const setSelectedProcessIds = useCallback((valOrFn: any) => {
    _setSelectedProcessIds((prev) => {
      const next = typeof valOrFn === "function" ? valOrFn(prev) : valOrFn;
      processIdsRef.current = next;
      return next;
    });
  }, []);

  const [showCustomerError, setShowCustomerError] = useState(false);
  const [showTypeError, setShowTypeError] = useState(false);
  const [showCodeError, setShowCodeError] = useState(false);
  const [showMaterialError, setShowMaterialError] = useState(false);
  const [showProcessError, setShowProcessError] = useState(false);
  const [showInProcessError, setShowInProcessError] = useState(false);
  const [showThanhPhamProcessError, setShowThanhPhamProcessError] = useState(false);
  const [showLengthError, setShowLengthError] = useState(false);
  const [showWidthError, setShowWidthError] = useState(false);
  const [serverFieldErrors, setServerFieldErrors] = useState<Record<string, string>>({});

  const form = useForm({
    defaultValues: {
      productCode: defaultValues?.productCode ?? "",
      productName: defaultValues?.productName ?? "",
      customerId: defaultValues?.customerId ?? "",
      customerName: defaultValues?.customerName ?? "",
      typeId: defaultValues?.typeId ?? "",
      materialIds: defaultValues?.materialIds ?? [],
      processIds: defaultValues?.processIds ?? [],
      unit: defaultValues?.unit || "Cái",
      packingSpec: defaultValues?.packingSpec ?? "",
      notes: (defaultValues as any)?.notes ?? "",
      pcsPerBox:
        defaultValues?.pcsPerBox !== null &&
        defaultValues?.pcsPerBox !== undefined &&
        (defaultValues?.pcsPerBox as any) !== "" &&
        Number(defaultValues.pcsPerBox) > 0
          ? Math.round(Number(defaultValues.pcsPerBox))
          : null,
      price: defaultValues?.price ?? 0,
      costPrice: defaultValues?.costPrice ?? 0,
      process: defaultValues?.process ?? "",
      specs: defaultValues?.specs ?? "",
      image: defaultValues?.image ?? "",
      tags: defaultValues?.tags ?? "",
      active: defaultValues?.active ?? true,
      isComposite: defaultValues?.isComposite ?? false,
      components: defaultValues?.components ?? [],
      processConfigs: defaultValues?.processConfigs ?? [],
      productMaterials: defaultValues?.productMaterials ?? [],
      length: defaultValues?.length ?? 0,
      width: defaultValues?.width ?? 0,
      height: defaultValues?.height ?? 0,
      printFormat: (() => {
        if (defaultValues?.printFormat) return defaultValues.printFormat;
        try {
          const s = typeof defaultValues?.specs === "string" && defaultValues.specs.startsWith("{")
            ? JSON.parse(defaultValues.specs)
            : defaultValues?.specs;
          return s?.printSize || "";
        } catch {
          return "";
        }
      })(),
      cutFormat: (() => {
        if (defaultValues?.cutFormat) return defaultValues.cutFormat;
        try {
          const s = typeof defaultValues?.specs === "string" && defaultValues.specs.startsWith("{")
            ? JSON.parse(defaultValues.specs)
            : defaultValues?.specs;
          return s?.purchaseSize || "";
        } catch {
          return "";
        }
      })(),
      outs: defaultValues?.outs ?? 1,
      flatLength: (() => {
        if (defaultValues?.flatLength && Number(defaultValues.flatLength) > 0) return Number(defaultValues.flatLength);
        try {
          const s = typeof defaultValues?.specs === "string" && defaultValues.specs.startsWith("{")
            ? JSON.parse(defaultValues.specs)
            : defaultValues?.specs;
          return Number(s?.flatLength || s?.flength || 0);
        } catch {
          return 0;
        }
      })(),
      flatWidth: (() => {
        if (defaultValues?.flatWidth && Number(defaultValues.flatWidth) > 0) return Number(defaultValues.flatWidth);
        try {
          const s = typeof defaultValues?.specs === "string" && defaultValues.specs.startsWith("{")
            ? JSON.parse(defaultValues.specs)
            : defaultValues?.specs;
          return Number(s?.flatWidth || s?.fwidth || 0);
        } catch {
          return 0;
        }
      })(),
    },
    // @ts-ignore
    validatorAdapter: zodValidator(),
    validators: { onChange: productFormSchema as any },
    onSubmit: async ({ value }) => {
      // 0. Kiểm tra 3 trường bắt buộc hàng đầu: Khách hàng, N.Sản phẩm, Mã sản phẩm
      const isCustomerInvalid = !value.customerId || !String(value.customerId).trim();
      const isTypeInvalid = !value.typeId || !String(value.typeId).trim();
      const rawCode = String(value.productCode ?? "").trim();
      const isEditMode = Boolean(defaultValues?.productCode || productId);
      const isCodeFormatInvalid = rawCode.length > 0 && !/^[a-zA-Z0-9\-_]+$/.test(rawCode);
      const isCodeEmptyInvalid = !rawCode && (isEditMode || isCodeManual || isCustomerInvalid);
      const isCodeInvalid = isCodeFormatInvalid || isCodeEmptyInvalid;

      // 1. Kiểm tra Dài (mm) và Rộng (mm)
      const isLengthInvalid = !value.length || Number(value.length) <= 0;
      const isWidthInvalid = !value.width || Number(value.width) <= 0;

      // 2. Kiểm tra Loại vật tư (specs.materialTypeIds HOẶC materialIds HOẶC productMaterials)
      let hasMaterialTypes = false;
      try {
        const rawSpecs = value.specs;
        const parsed = typeof rawSpecs === "string" && rawSpecs.trim().startsWith("{")
          ? JSON.parse(rawSpecs)
          : (typeof rawSpecs === "object" && rawSpecs ? rawSpecs : null);
        if (parsed && Array.isArray(parsed.materialTypeIds) && parsed.materialTypeIds.length > 0) {
          hasMaterialTypes = true;
        }
      } catch {}
      const hasMaterials = hasMaterialTypes ||
        (value.materialIds && value.materialIds.length > 0) ||
        (value.productMaterials && value.productMaterials.length > 0);

      // 3. Kiểm tra Quy trình sản xuất
      const currentProcIds = processIdsRef.current || [];
      const hasProcesses =
        currentProcIds.length > 0 ||
        (value.processIds && value.processIds.length > 0) ||
        (value.processConfigs && value.processConfigs.length > 0);

      // 4. Bất biến quy trình sản xuất bao bì: Bắt buộc ít nhất 1 IN và 1 THANH_PHAM (cho sản phẩm đơn)
      const isSingleProduct = !value.isComposite;
      const inProcesses = currentProcIds.filter((pid) => {
        const opt = processLookup.options.find((o) => o.id === pid);
        return opt ? getProcessGroupKey(opt) === "IN" : false;
      });
      const thanhPhamProcesses = currentProcIds.filter((pid) => {
        const opt = processLookup.options.find((o) => o.id === pid);
        return opt ? getProcessGroupKey(opt) === "THANH_PHAM" : false;
      });
      const hasInProcess = !isSingleProduct || inProcesses.length > 0;
      const hasThanhPhamProcess = !isSingleProduct || thanhPhamProcesses.length > 0;

      if (
        isCustomerInvalid ||
        isTypeInvalid ||
        isCodeInvalid ||
        isLengthInvalid ||
        isWidthInvalid ||
        !hasMaterials ||
        !hasProcesses ||
        !hasInProcess ||
        !hasThanhPhamProcess
      ) {
        if (isCustomerInvalid) setShowCustomerError(true);
        if (isTypeInvalid) setShowTypeError(true);
        if (isCodeInvalid) setShowCodeError(true);
        if (isLengthInvalid) setShowLengthError(true);
        if (isWidthInvalid) setShowWidthError(true);
        if (!hasMaterials) setShowMaterialError(true);
        if (!hasProcesses) setShowProcessError(true);
        if (!hasInProcess) setShowInProcessError(true);
        if (!hasThanhPhamProcess) setShowThanhPhamProcessError(true);

        if (isCustomerInvalid) {
          const el = document.getElementById("customerId");
          el?.focus();
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (isTypeInvalid) {
          const el = document.getElementById("typeId");
          el?.focus();
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (isCodeInvalid) {
          const el = document.getElementById("productCode");
          el?.focus();
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (isLengthInvalid) {
          const el = document.getElementById("length");
          el?.focus();
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (isWidthInvalid) {
          const el = document.getElementById("width");
          el?.focus();
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        } else if (!hasMaterials) {
          const el = document.getElementById("materialTypeContainer");
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
          el?.querySelector("input")?.focus();
        } else if (!hasInProcess) {
          const el = document.getElementById("process-group-IN");
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
          (el?.querySelector("button, input") as HTMLElement)?.focus();
        } else if (!hasThanhPhamProcess) {
          const el = document.getElementById("process-group-THANH_PHAM");
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
          (el?.querySelector("button, input") as HTMLElement)?.focus();
        } else if (!hasProcesses) {
          const el = document.getElementById("processIds");
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
          el?.querySelector("input")?.focus();
        }

        // Liệt kê đầy đủ tất cả mục còn thiếu để người dùng sửa một lần
        const missingItems: string[] = [];
        if (isCustomerInvalid) missingItems.push("Khách hàng: Vui lòng chọn khách hàng cần nhập lại");
        if (isTypeInvalid) missingItems.push("N.Sản phẩm: Vui lòng chọn nhóm sản phẩm cần nhập lại");
        if (isCodeInvalid) {
          missingItems.push(
            isCodeFormatInvalid
              ? "Mã sản phẩm: Không được chứa khoảng trắng và ký tự đặc biệt"
              : "Mã sản phẩm: Vui lòng nhập mã sản phẩm cần nhập lại",
          );
        }
        if (isLengthInvalid) missingItems.push("Dài (mm) phải lớn hơn 0");
        if (isWidthInvalid) missingItems.push("Rộng (mm) phải lớn hơn 0");
        if (!hasMaterials) missingItems.push("Chọn ít nhất 1 loại vật tư");
        if (!hasProcesses) missingItems.push("Chọn quy trình sản xuất");
        if (!hasInProcess) missingItems.push("Quy trình cần có ít nhất 1 công đoạn In ấn");
        if (!hasThanhPhamProcess) missingItems.push("Quy trình cần có ít nhất 1 công đoạn Thành phẩm");
        const otherMessages = collectFormErrorMessages(form.state as any).filter(
          (m) => !missingItems.includes(m),
        );
        const allMessages = [...missingItems, ...otherMessages];
        toast.error(
          `Còn ${allMessages.length} mục cần nhập lại:\n` + allMessages.map((m) => `• ${m}`).join("\n"),
          { duration: 9000 },
        );
        return false;
      }

      let code = value.productCode;
      if (!code && value.customerId) {
        try {
          const gen = await trpcClient.products.generateCode.query({
            customerId: value.customerId,
          });
          if (gen?.code) code = gen.code;
        } catch {
          /* ignore */
        }
      }
      if (!code) {
        code = `SP-${Date.now().toString().slice(-6)}`;
      }
      const submitData = {
        ...value,
        productCode: code,
        processIds: processIdsRef.current,
      } as ProductFormData & { processIds: string[] };
      try {
        setServerFieldErrors({});
        const success = await onSubmit(submitData);
        if (success !== false) {
          setIsSubmitSuccessful(true);
        }
      } catch (err: any) {
        const msg = String(err?.message || "");
        const nextErrors: Record<string, string> = {};
        let targetFieldId = "";
        if (/mã sản phẩm|product_code|productCode|trùng giá trị duy nhất/i.test(msg)) {
          nextErrors.productCode = msg.includes("Mã sản phẩm")
            ? msg
            : `Mã sản phẩm "${code}" đã được sử dụng. Vui lòng nhập lại mã sản phẩm khác.`;
          targetFieldId = "productCode";
        } else if (/tên sản phẩm|productName/i.test(msg)) {
          nextErrors.productName = msg;
          targetFieldId = "productName";
        } else if (/khách hàng|customerId/i.test(msg)) {
          nextErrors.customerId = msg;
          targetFieldId = "customerId";
        } else if (/nhóm sản phẩm|loại sản phẩm|typeId/i.test(msg)) {
          nextErrors.typeId = msg;
          targetFieldId = "typeId";
        } else if (/chiều dài|length/i.test(msg)) {
          nextErrors.length = msg;
          setShowLengthError(true);
          targetFieldId = "length";
        } else if (/chiều rộng|width/i.test(msg)) {
          nextErrors.width = msg;
          setShowWidthError(true);
          targetFieldId = "width";
        }
        if (Object.keys(nextErrors).length > 0) {
          setServerFieldErrors(nextErrors);
          if (targetFieldId) {
            requestAnimationFrame(() => {
              const el = document.getElementById(targetFieldId) as HTMLInputElement | null;
              el?.scrollIntoView({ behavior: "smooth", block: "center" });
              el?.focus();
              if (el && typeof el.select === "function") el.select();
            });
          }
        }
        return false;
      }
    },
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const vals = form.state.values;
    const isCustomerInvalid = !vals.customerId || !String(vals.customerId).trim();
    const isTypeInvalid = !vals.typeId || !String(vals.typeId).trim();
    const rawCode = String(vals.productCode ?? "").trim();
    const isEditMode = Boolean(defaultValues?.productCode || productId);
    const isCodeInvalid =
      (rawCode.length > 0 && !/^[a-zA-Z0-9\-_]+$/.test(rawCode)) ||
      (!rawCode && (isEditMode || isCodeManual || isCustomerInvalid));

    if (isCustomerInvalid) setShowCustomerError(true);
    if (isTypeInvalid) setShowTypeError(true);
    if (isCodeInvalid) setShowCodeError(true);
    if (!vals.length || Number(vals.length) <= 0) setShowLengthError(true);
    if (!vals.width || Number(vals.width) <= 0) setShowWidthError(true);
    form.handleSubmit();
    requestAnimationFrame(() => focusFirstError(form.state as any));
  };

  const isDirty = useStore(form.store, (state: any) => state.isDirty);
  const isSubmittingForm = useStore(
    form.store,
    (state: any) => state.isSubmitting,
  );
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  useFormBlocker(
    enableBlocker && isDirty && !isSubmittingForm && !isSubmitting && !isSubmitSuccessful,
  );

  const currentSpecs = useStore(form.store, (state: any) => state.values.specs);
  const materialTypeIdsParam = useMemo(() => {
    try {
      let p: any = {};
      if (typeof currentSpecs === "object" && currentSpecs !== null) p = currentSpecs;
      else if (typeof currentSpecs === "string" && currentSpecs.trim().startsWith("{")) {
        p = JSON.parse(currentSpecs);
      }
      if (Array.isArray(p.materialTypeIds) && p.materialTypeIds.length > 0) {
        return p.materialTypeIds.join(",");
      }
    } catch {}
    return undefined;
  }, [currentSpecs]);

  const customerLookup = useCustomerLookup();
  const baseMaterialLookup = useMaterialLookup(true, undefined, 100, materialTypeIdsParam);
  // Vật tư đã chọn nhưng nằm ngoài danh sách tra cứu (lọc theo Loại vật tư, tối đa 100 dòng): nạp riêng để chip
  // Vật tư cấu thành hiện tên thay vì mã id và ô Vật tư đầu vào ở bảng công đoạn có vật tư để chọn (FB-8D2LZP)
  const selectedMaterialIds = useStore(form.store, (state: any) => state.values.materialIds as string[] | undefined);
  const missingMaterialIds = useMemo(
    () =>
      Array.from(new Set((selectedMaterialIds || []).filter(Boolean))).filter(
        (id) => !baseMaterialLookup.options.some((o) => o.id === id),
      ),
    [selectedMaterialIds, baseMaterialLookup.options],
  );
  const missingMaterialQueries = useQueries({
    queries: baseMaterialLookup.isLoading
      ? []
      : missingMaterialIds.map((id) => ({
          queryKey: ["material", id],
          queryFn: () => trpcClient.materials.getById.query({ id }),
          staleTime: 60 * 1000,
        })),
  });
  const materialLookup = useMemo(() => {
    const extra = missingMaterialQueries
      .map((q) => q.data as any)
      .filter((m) => m?.id && !baseMaterialLookup.options.some((o) => o.id === m.id))
      .map((m) => ({ id: m.id, label: m.name || m.materialId || m.id, meta: m.materialId || undefined, original: m }));
    if (extra.length === 0) return baseMaterialLookup;
    return { ...baseMaterialLookup, options: [...baseMaterialLookup.options, ...extra] };
  }, [baseMaterialLookup, missingMaterialQueries]);
  const materialTypeLookup = useMaterialTypeLookup();
  const productTypeLookup = useProductTypeLookup();
  const processLookup = useProcessLookup();
  const unitLookup = useUnitLookup();

  // Active open popover for process group multi-select (TRUOC_IN, IN, SAU_IN, THANH_PHAM)

  // Collapsible toggle for advanced process configs & costs (default closed for Sales)
  const [isProcessConfigsOpen, setIsProcessConfigsOpen] = useState(false);

  // Filter process options for material configs to only show processes selected for this product
  const filteredProcessOptions = useMemo(() => {
    return processLookup.options.filter((o) => selectedProcessIds.includes(o.id));
  }, [processLookup.options, selectedProcessIds]);

  // Listen to customerId changes to filter the copy dropdown
  const currentCustomerId = useStore(
    form.store,
    (state: any) => state.values.customerId as string,
  );
  const productLookup = useProductLookup(currentCustomerId || "_BLOCK_");

  const qc = useQueryClient();
  const { hasPermission } = useRoleAccess();
  
  const packingSpecSuggestions = useConfigSuggestions('PACKING_SPECS', [
    "Thùng carton 3 lớp",
    "Thùng carton 5 lớp",
    "Đóng màng co",
    "Buộc dây nilon",
    "Đóng gói giấy Kraft",
    "Đóng pallet",
  ]);

  const [customerModal, setCustomerModal] = useState<{
    open: boolean;
    name: string;
  }>({ open: false, name: "" });
  const [materialModal, setMaterialModal] = useState<{
    open: boolean;
    name: string;
    typeId?: string;
  }>({ open: false, name: "" });
  const [materialTypeModal, setMaterialTypeModal] = useState<{
    open: boolean;
    name: string;
  }>({ open: false, name: "" });
  const [processModal, setProcessModal] = useState<{
    open: boolean;
    name: string;
    groupKey: string;
  }>({ open: false, name: "", groupKey: "" });

  const isComposite = useStore(
    form.store,
    (state: any) => state.values.isComposite,
  );

  const [isCodeManual, setIsCodeManual] = useState(false);
  const queryClient = useQueryClient();

  // Auto-generate product code on mount if initial customerId is provided
  useEffect(() => {
    const initCustomerId = defaultValues?.customerId;
    if (initCustomerId && !defaultValues?.productCode && !isCodeManual) {
      trpcClient.products.generateCode
        .query({ customerId: initCustomerId })
        .then((result) => {
          if (result?.code && !form.state.values.productCode) {
            form.setFieldValue("productCode", result.code);
          }
        })
        .catch(() => {});
    }
  }, [defaultValues?.customerId, defaultValues?.productCode, form]);

  // Process multi-tag has been hoisted above form

  const buildDescription = (
    matIds?: string[],
    procIds?: string[],
    specsStr?: any,
  ) => {
    const mids = matIds ?? (form.state.values.materialIds as string[]) ?? [];
    const pids = procIds ?? selectedProcessIds;
    const currentSpecsStr = specsStr ?? form.state.values.specs ?? "";

    let currentSpecsObj: any = {};
    try {
      if (typeof currentSpecsStr === "object" && currentSpecsStr !== null)
        currentSpecsObj = currentSpecsStr;
      else if (
        typeof currentSpecsStr === "string" &&
        currentSpecsStr.trim().startsWith("{")
      )
        currentSpecsObj = JSON.parse(currentSpecsStr);
      else if (typeof currentSpecsStr === "string" && currentSpecsStr)
        currentSpecsObj = { legacy: currentSpecsStr };
    } catch {}

    const parts: string[] = [];

    // 1. Loại vật tư
    const matTypeIds: string[] = Array.isArray(currentSpecsObj.materialTypeIds)
      ? currentSpecsObj.materialTypeIds
      : [];
    const matTypeNames = matTypeIds
      .map(
        (id: string) =>
          materialTypeLookup.options.find((o) => o.id === id)?.label,
      )
      .filter(Boolean);

    if (matTypeNames.length > 0) {
      parts.push(...(matTypeNames as string[]));
    } else {
      const matNames = mids
        .map((id: string) => {
          const opt = materialLookup.options.find((o) => o.id === id);
          if (!opt) return null;
          if (opt.original?.typeId) {
            const typeOpt = materialTypeLookup.options.find(
              (o) => o.id === opt.original.typeId,
            );
            if (typeOpt) return typeOpt.label;
          }
          return opt.label.split("_")[0].trim();
        })
        .filter(Boolean);
      const uniqueMatNames = [...new Set(matNames)];
      if (uniqueMatNames.length) parts.push(...(uniqueMatNames as string[]));
    }

    // 2. Quy cách (kích thước) sản phẩm
    let sizeStr = "";
    if (currentSpecsObj.size) sizeStr = currentSpecsObj.size;
    else if (currentSpecsObj.legacy) sizeStr = currentSpecsObj.legacy;

    if (sizeStr) {
      parts.push(sizeStr);
    }

    // 3. Quy trình sản xuất — đúng thứ tự quy trình người dùng chọn / kéo thả (chuỗi luôn khớp danh sách công đoạn, FB-WKEZ0Q)
    const selectedProcesses = pids
      .map((id) => processLookup.options.find((o) => o.id === id))
      .filter(Boolean);

    const processNames = selectedProcesses.map((p) => p?.label);
    if (processNames.length > 0) parts.push(...(processNames as string[]));

    return parts.join(", ");
  };

  // Chuỗi "Quy trình thực hiện" đã lưu là bản tự sinh nhưng sai thứ tự (cùng các mục) → xếp lại theo thứ tự công nghệ.
  // Chuỗi người dùng tự sửa tay (khác mục) thì giữ nguyên (FB-WKEZ0Q).
  const processTextFixedRef = useRef(false);
  useEffect(() => {
    // Chờ danh mục công đoạn / loại vật tư nạp xong, nếu không chuỗi tự sinh thiếu mục và sẽ không khớp
    if (processTextFixedRef.current || processLookup.options.length === 0 || materialTypeLookup.options.length === 0 || selectedProcessIds.length === 0) return;
    processTextFixedRef.current = true;
    const stored = String(form.state.values.process || "").trim();
    if (!stored) return;
    const auto = buildDescription();
    const parts = (t: string) => t.split(",").map((x) => x.trim()).filter(Boolean);
    const a = parts(stored);
    const b = parts(auto);
    if (stored !== auto && a.length === b.length && [...a].sort().join("|") === [...b].sort().join("|")) {
      form.setFieldValue("process", auto as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processLookup.options.length, materialTypeLookup.options.length, selectedProcessIds.length]);

  // Helper: Smart Auto-Binding — Nếu chỉ có đúng 1 loại vật tư và 1 công đoạn IN, tự động gán vật tư đó vào công đoạn in
  const autoBindSingleMaterialToPrintProcess = (
    currentMaterials: any[],
    procIds: string[],
  ): any[] => {
    if (!currentMaterials || currentMaterials.length !== 1 || !procIds || procIds.length === 0) {
      return currentMaterials;
    }
    const inProcesses = procIds.filter((pid) => {
      const opt = processLookup.options.find((o) => o.id === pid);
      const groupKey = opt?.original?.groupKey || opt?.original?.group_key || opt?.original?.group;
      return groupKey === "IN" || opt?.label?.toLowerCase().includes("in ");
    });

    if (inProcesses.length === 1 && currentMaterials[0]) {
      const targetProcessId = inProcesses[0];
      if (currentMaterials[0].processId !== targetProcessId) {
        return [{ ...currentMaterials[0], processId: targetProcessId }];
      }
    }
    return currentMaterials;
  };

  // Helper: Đồng bộ khổ giấy mua (cutFormat) & khổ in (printFormat) theo danh sách vật tư đã chọn
  const updatePaperSizesFromMaterialList = (matIds: string[]) => {
    if (!matIds || matIds.length === 0) return;
    const matObjects = matIds
      .map((mId) => materialLookup.options.find((o) => o.id === mId)?.original)
      .filter(Boolean);

    // Tìm vật tư giấy (có width & length hoặc name chứa kích thước)
    const paperMat = [...matObjects].reverse().find((mat) => {
      let w = mat.width ? Number(mat.width) : 0;
      let l = mat.length ? Number(mat.length) : 0;
      if (w === 0 || l === 0) {
        const match = (mat.name || "").match(/(\d+)\s*[*xX]\s*(\d+)/);
        if (match) {
          w = Number(match[1]);
          l = Number(match[2]);
        }
      }
      return w > 0 && l > 0;
    });

    if (paperMat) {
      let w = paperMat.width ? Number(paperMat.width) : 0;
      let l = paperMat.length ? Number(paperMat.length) : 0;
      if (w === 0 || l === 0) {
        const match = (paperMat.name || "").match(/(\d+)\s*[*xX]\s*(\d+)/);
        if (match) {
          w = Number(match[1]);
          l = Number(match[2]);
        }
      }

      if (w > 0 && l > 0) {
        const sizeStr = normalizePaperDimension(`${w}x${l}`);
        const rawSpecs = form.state.values.specs as string;
        let parsed: any = {};
        try {
          if (typeof rawSpecs === "string" && rawSpecs.startsWith("{")) parsed = JSON.parse(rawSpecs);
          else if (typeof rawSpecs === "object" && rawSpecs) parsed = rawSpecs;
        } catch {}

        parsed.printSize = sizeStr;
        parsed.purchaseSize = sizeStr;
        form.setFieldValue("specs", JSON.stringify(parsed));
        form.setFieldValue("printFormat", sizeStr);
        form.setFieldValue("cutFormat", sizeStr);
      }
    }
  };

  // Quy trình theo đúng thứ tự người dùng chọn / kéo thả (chọn trước đứng trước); muốn chuẩn công nghệ thì bấm Sắp theo chuẩn
  const applyProcessIds = (next: string[]) => {
    const addedIds = next.filter((id) => !selectedProcessIds.includes(id));

    setSelectedProcessIds(next);
    form.setFieldValue("processIds", next);
    form.setFieldValue("process", buildDescription(undefined, next) as any);

    if (next.length > 0) setShowProcessError(false);
    const hasIn = next.some((pid) => {
      const opt = processLookup.options.find((o) => o.id === pid);
      return opt ? getProcessGroupKey(opt) === "IN" : false;
    });
    if (hasIn) setShowInProcessError(false);

    const hasThanhPham = next.some((pid) => {
      const opt = processLookup.options.find((o) => o.id === pid);
      return opt ? getProcessGroupKey(opt) === "THANH_PHAM" : false;
    });
    if (hasThanhPham) setShowThanhPhamProcessError(false);

    // Smart Auto-Binding khi toggle công đoạn
    const currentMaterials = form.state.values.productMaterials || [];
    const autoBound = autoBindSingleMaterialToPrintProcess(currentMaterials, next);
    if (autoBound !== currentMaterials) {
      form.setFieldValue("productMaterials", autoBound);
    }

    const currentConfigs = form.state.values.processConfigs || [];
    const newConfigs = [...currentConfigs];
    for (const processId of addedIds) {
      if (newConfigs.some((c) => c.processId === processId)) continue;
      const proc = processLookup.options.find(
        (o) => o.id === processId,
      )?.original;

      const defaultBatchCost = proc?.setupCost ? Number(proc.setupCost) : (proc?.defaultBatchCost ? Number(proc.defaultBatchCost) : (proc?.group === 'IN' ? 400000 : 200000));
      const defaultUnitCost = proc?.unitCost ? Number(proc.unitCost) : (proc?.defaultUnitCost ? Number(proc.defaultUnitCost) : 0);
      const defaultSetupWaste = proc?.minWasteQty ? Number(proc.minWasteQty) : (proc?.defaultSetupWaste ? Number(proc.defaultSetupWaste) : (proc?.group === 'IN' ? 100 : 50));
      const defaultRunWastePct = proc?.wastePercent ? Number(proc.wastePercent) : (proc?.defaultRunWastePct ? Number(proc.defaultRunWastePct) : 2.0);

      newConfigs.push(
        {
          processId,
          setupTimeMinutes: proc?.standardTime ? Number(proc.standardTime) : 30,
          speedPerHour: 0,
          runWastePercent: defaultRunWastePct,
          setupWasteQty: defaultSetupWaste,
          isOutsourced: false,
          unitCost: defaultUnitCost,
          batchCost: defaultBatchCost,
        },
      );
    }
    // Đồng bộ thứ tự processConfigs theo đúng thứ tự quy trình; công đoạn bị bỏ thì bỏ cấu hình
    const sortedConfigs = next
      .map((pid) => newConfigs.find((c) => c.processId === pid))
      .filter(Boolean) as any[];
    form.setFieldValue("processConfigs", sortedConfigs);
  };

  const handleMoveProcess = (index: number, direction: 'UP' | 'DOWN') => {
    const targetIndex = direction === 'UP' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= selectedProcessIds.length) return;

    const nextIds = [...selectedProcessIds];
    const [movedId] = nextIds.splice(index, 1);
    nextIds.splice(targetIndex, 0, movedId);

    setSelectedProcessIds(nextIds);
    form.setFieldValue("processIds", nextIds);
    form.setFieldValue("process", buildDescription(undefined, nextIds) as any);

    const currentConfigs = form.state.values.processConfigs || [];
    const nextConfigs = nextIds
      .map((pid) => currentConfigs.find((c) => c.processId === pid))
      .filter(Boolean) as any[];
    form.setFieldValue("processConfigs", nextConfigs);
  };

  const handleNormalizeProcessOrder = () => {
    const next = sortPackagingProcessIds(selectedProcessIds, processLookup.options);
    setSelectedProcessIds(next);
    form.setFieldValue("processIds", next);
    form.setFieldValue("process", buildDescription(undefined, next) as any);

    const currentConfigs = form.state.values.processConfigs || [];
    const sortedConfigs = next
      .map((pid) => currentConfigs.find((c) => c.processId === pid))
      .filter(Boolean) as any[];
    form.setFieldValue("processConfigs", sortedConfigs);
    toast.success("Đã chuẩn hóa thứ tự công đoạn theo chuỗi công nghệ bao bì!");
  };

  // TextField helper (changed to render function to prevent remounts)
  const renderTextField = ({
    name,
    label,
    required,
    placeholder,
    type = "text",
    className,
    onChangeHook,
  }: {
    name: keyof ProductFormData;
    label: string;
    required?: boolean;
    placeholder?: string;
    type?: string;
    className?: string;
    onChangeHook?: (val: string) => void;
  }) => (
    <form.Field name={name as any}>
      {(field) => {
        const isCodeField = name === "productCode";
        const rawVal = String(field.state.value ?? "").trim();
        const codeFormatErr =
          isCodeField && rawVal.length > 0 && !/^[a-zA-Z0-9\-_]+$/.test(rawVal)
            ? "Mã sản phẩm không được chứa khoảng trắng và ký tự đặc biệt (cần nhập lại)"
            : undefined;
        const codeEmptyErr =
          isCodeField && showCodeError && rawVal.length === 0
            ? "Vui lòng nhập mã sản phẩm cần nhập lại"
            : undefined;
        const externalErr =
          serverFieldErrors[name as string] || codeFormatErr || codeEmptyErr;
        return (
          <TanstackFieldWrapper
            field={field}
            label={label}
            required={required}
            className={className}
            externalError={externalErr}
          >
            <TanstackFormControl>
              <Input
                id={name}
                name={name}
                type={type}
                value={(field.state.value as any) ?? ""}
                placeholder={placeholder}
                className="h-9 text-sm"
                onChange={(e) => {
                  if (isCodeField && e.target.value.trim().length > 0) {
                    setShowCodeError(false);
                  }
                  if (serverFieldErrors[name as string]) {
                    setServerFieldErrors((prev) => {
                      const next = { ...prev };
                      delete next[name as string];
                      return next;
                    });
                  }
                  const val =
                    type === "number"
                      ? e.target.value
                        ? Number(e.target.value)
                        : undefined
                      : e.target.value;
                  (field.handleChange as any)(val);
                  if (onChangeHook) onChangeHook(e.target.value);
                }}
                onBlur={field.handleBlur}
              />
            </TanstackFormControl>
          </TanstackFieldWrapper>
        );
      }}
    </form.Field>
  );


  return (
    <form onSubmit={handleFormSubmit} className="space-y-5">
      {/* ═══ SECTION 1: Thông tin sản phẩm ═══ */}
      <div className="border rounded-lg bg-white shadow-sm p-4">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-bold text-emerald-700">
            Thông tin sản phẩm
          </h3>
          {!defaultValues?.productCode && (
            <div className="w-[350px]">
              <SharedCombobox
                type="product"
                label=""
                value=""
                onChange={async (id) => {
                  if (!id) return;
                  try {
                    const original: any =
                      await trpcClient.products.getById.query({ id });
                    if (original) {
                      const cloned = buildClonedProductFormData(original);

                      // 1. Tên sản phẩm & Mã (Reset chuỗi rỗng)
                      form.setFieldValue("productName", cloned.productName || "");

                      // 2. Khách hàng & Loại sản phẩm
                      if (cloned.customerId) {
                        form.setFieldValue("customerId", cloned.customerId);
                        form.setFieldValue("customerName", cloned.customerName || "");
                      }
                      if (cloned.typeId) {
                        form.setFieldValue("typeId", cloned.typeId);
                      }

                      // 3. Đơn vị, Quy cách & Giá
                      form.setFieldValue("unit", cloned.unit || "cái");
                      form.setFieldValue("packingSpec", cloned.packingSpec || "");
                      form.setFieldValue("pcsPerBox", cloned.pcsPerBox ?? null);
                      form.setFieldValue("price", cloned.price || 0);
                      form.setFieldValue("costPrice", cloned.costPrice || 0);

                      // 4. Kích thước (Dimensions) & Bài bình (Imposition)
                      form.setFieldValue("length", cloned.length ?? 0);
                      form.setFieldValue("width", cloned.width ?? 0);
                      form.setFieldValue("height", cloned.height ?? 0);
                      form.setFieldValue("flatLength", cloned.flatLength ?? 0);
                      form.setFieldValue("flatWidth", cloned.flatWidth ?? 0);
                      form.setFieldValue("printFormat", cloned.printFormat || "");
                      form.setFieldValue("cutFormat", cloned.cutFormat || "");
                      form.setFieldValue("outs", cloned.outs ?? 1);

                      // 5. Specs, mô tả & tags
                      if (cloned.specs) {
                        form.setFieldValue("specs", cloned.specs);
                      }
                      if (cloned.process) form.setFieldValue("process", cloned.process);
                      if (cloned.tags) form.setFieldValue("tags", cloned.tags);
                      if (cloned.image) form.setFieldValue("image", cloned.image);

                      // 6. Sản phẩm combo (Composite components)
                      form.setFieldValue("isComposite", cloned.isComposite || false);
                      form.setFieldValue("components", cloned.components || []);

                      // 7. Quy trình sản xuất & 8 thông số máy (Processes & Configs)
                      setSelectedProcessIds(cloned.processIds || []);
                      form.setFieldValue("processIds", cloned.processIds || []);
                      form.setFieldValue("processConfigs", cloned.processConfigs || []);

                      // 8. Vật tư & BOM (Materials & Standard Quantities)
                      form.setFieldValue("materialIds", cloned.materialIds || []);
                      form.setFieldValue("productMaterials", cloned.productMaterials || []);
                      // Quy trình thực hiện: tính lại theo thứ tự công nghệ, không chép chuỗi cũ có thể sai thứ tự (FB-WKEZ0Q)
                      if ((cloned.processIds || []).length > 0) {
                        form.setFieldValue("process", buildDescription(cloned.materialIds || [], cloned.processIds, cloned.specs) as any);
                      }

                      toast.success(
                        `Đã sao chép 100% bài bình, BOM và quy trình từ "${original.productName}"`
                      );

                      // Tự động focus con trỏ vào ô Tên sản phẩm
                      setTimeout(() => {
                        const input = document.querySelector(
                          'input[name="productName"]',
                        ) as HTMLInputElement | null;
                        if (input) {
                          input.focus();
                          input.select();
                        }
                      }, 100);
                    }
                  } catch (e: any) {
                    toast.error(`Lỗi tải dữ liệu tham chiếu: ${e.message}`);
                  }
                }}
                placeholder={
                  currentCustomerId
                    ? "Chọn SP để copy..."
                    : "Vui lòng chọn Khách hàng trước..."
                }
                disabled={!currentCustomerId}
              />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_220px] gap-6">
          {/* FORM DATA */}
          <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-x-4 gap-y-4 content-start">
            {/* R1: Khách hàng, N.Sản phẩm, Mã SP */}
            <div className="sm:col-span-2 xl:col-span-2">
              <form.Field name="customerId">
                {(field) => {
                  const isCustomerLocked = lockedFields?.includes("customerId");
                  const customerErrMsg =
                    serverFieldErrors.customerId ||
                    (showCustomerError && !String(field.state.value || "").trim()
                      ? "Vui lòng chọn khách hàng cần nhập lại"
                      : undefined);
                  const hasCustomerErr =
                    Boolean(field.state.meta.errors.length) || Boolean(customerErrMsg);
                  return (
                    <TanstackFieldWrapper
                      field={field}
                      label="Khách hàng"
                      required
                      externalError={customerErrMsg}
                    >
                      <div className="relative">
                        <SharedCombobox
                          id="customerId"
                          name="customerId"
                          type="customer"
                          required={true}
                          value={(field.state.value as string) || ""}
                          valueLabel={
                            form.getFieldValue("customerName") ||
                            defaultValues?.customerName ||
                            (defaultValues as any)?.customer?.shortName ||
                            (defaultValues as any)?.customer?.name
                          }
                          onChange={async (id) => {
                            if (isCustomerLocked) return;
                            if (id) {
                              setShowCustomerError(false);
                              if (serverFieldErrors.customerId) {
                                setServerFieldErrors((prev) => {
                                  const next = { ...prev };
                                  delete next.customerId;
                                  return next;
                                });
                              }
                            }
                            field.handleChange(id);
                            if (id && !defaultValues?.productCode) {
                              if (isCodeManual) return; // Không đè mã nếu user đã tự gõ
                              try {
                                const result =
                                  await trpcClient.products.generateCode.query({
                                    customerId: id,
                                  });
                                if (result?.code && !isCodeManual) {
                                  form.setFieldValue("productCode", result.code);
                                  setShowCodeError(false);
                                }
                              } catch {
                                /* Silently fail */
                              }
                            }
                          }}
                          onNameChange={(name) => {
                            if (!isCustomerLocked) {
                              form.setFieldValue("customerName", name);
                            }
                          }}
                          placeholder="Chọn KH trước..."
                          quickAddLabel="Thêm KH mới"
                          canAdd={!isCustomerLocked && hasPermission("customers", "create")}
                          breadcrumb={[...baseBreadcrumb, "Khách hàng"]}
                          onQuickAddClick={(name) =>
                            setCustomerModal({ open: true, name })
                          }
                          error={hasCustomerErr}
                          disabled={isCustomerLocked}
                          hideLabel
                        />
                        {isCustomerLocked && (
                          <div className="mt-1 flex items-center gap-1.5">
                            <Badge
                              variant="outline"
                              className="text-[11px] font-normal border-amber-300 bg-amber-50 text-amber-800"
                            >
                              🔒 Khách hàng liên kết (Khóa cứng theo ngữ cảnh)
                            </Badge>
                          </div>
                        )}
                      </div>
                    </TanstackFieldWrapper>
                  );
                }}
              </form.Field>
            </div>
            <div className="xl:col-span-1">
              <form.Field name="typeId">
                {(field) => {
                  const typeErrMsg =
                    serverFieldErrors.typeId ||
                    (showTypeError && !String(field.state.value || "").trim()
                      ? "Vui lòng chọn nhóm sản phẩm cần nhập lại"
                      : undefined);
                  const hasTypeErr =
                    Boolean(field.state.meta.errors.length) || Boolean(typeErrMsg);
                  return (
                    <TanstackFieldWrapper
                      field={field}
                      label="N.Sản phẩm"
                      required
                      externalError={typeErrMsg}
                    >
                      <SharedCombobox
                        id="typeId"
                        name="typeId"
                        type="productType"
                        value={(field.state.value as string) || ""}
                        valueLabel={(defaultValues as any)?.type?.code || (defaultValues as any)?.type?.group || (defaultValues as any)?.productType?.code || (defaultValues as any)?.productType?.group}
                        onChange={(id) => {
                          if (id) {
                            setShowTypeError(false);
                            if (serverFieldErrors.typeId) {
                              setServerFieldErrors((prev) => {
                                const next = { ...prev };
                                delete next.typeId;
                                return next;
                              });
                            }
                          }
                          field.handleChange(id);
                        }}
                        placeholder="Chọn nhóm..."
                        canAdd={hasPermission("products", "create")}
                        breadcrumb={[...baseBreadcrumb, "Loại sản phẩm"]}
                        error={hasTypeErr}
                        hideLabel
                      />
                    </TanstackFieldWrapper>
                  );
                }}
              </form.Field>
            </div>
            {renderTextField({
              name: "productCode",
              label: "Mã sản phẩm",
              required: true,
              placeholder: "Tự tạo",
              className: "xl:col-span-1",
              onChangeHook: () => setIsCodeManual(true),
            })}

            {/* R2: Tên sản phẩm, ĐVT, Trạng thái */}
            {renderTextField({
              name: "productName",
              label: "Tên sản phẩm",
              required: true,
              placeholder: "Thùng carton 3 lớp",
              className: "sm:col-span-2 xl:col-span-2",
            })}
            <div className="xl:col-span-1">
              <form.Field name="unit">
                {(field) => (
                  <div className="space-y-1">
                    <TanstackFieldWrapper
                      field={field}
                      label="Đơn vị tính"
                      required
                    >
                      <ComboboxConfigMaster
                        categoryCode="units"
                        value={(field.state.value as string) || ""}
                        onChange={(v) => field.handleChange(v)}
                        placeholder="Chọn..."
                        error={!!field.state.meta.errors.length}
                        hideLabel
                      />
                      {field.state.meta.errors.length > 0 && (
                        <div id="unit-debug-error" style={{ display: "none" }}>
                          {JSON.stringify(field.state.meta.errors)}
                        </div>
                      )}
                    </TanstackFieldWrapper>
                  </div>
                )}
              </form.Field>
            </div>
            <div className="xl:col-span-1 flex flex-col gap-2 pb-0 pt-6">
              <form.Field name="active">
                {(field) => (
                  <div className="flex items-center gap-2">
                    <Switch
                      id="active-switch"
                      checked={field.state.value as boolean}
                      onCheckedChange={(v) => field.handleChange(v)}
                    />
                    <Label
                      htmlFor="active-switch"
                      className="text-sm text-slate-600"
                    >
                      {(field.state.value as boolean) ? "Đang dùng" : "Ngưng"}
                    </Label>
                  </div>
                )}
              </form.Field>
              <form.Field name="isComposite">
                {(field) => (
                  <div className="flex items-center gap-2 mt-2">
                    <Switch
                      id="composite-switch"
                      checked={field.state.value as boolean}
                      onCheckedChange={(v) => field.handleChange(v)}
                    />
                    <Label
                      htmlFor="composite-switch"
                      className="text-sm font-semibold text-emerald-700"
                    >
                      SP Bộ/Combo
                    </Label>
                  </div>
                )}
              </form.Field>
            </div>

            {/* R3: Kích thước thành phẩm (DxRxC) & Khổ trải phôi */}
            <div className="col-span-2 sm:col-span-2 lg:col-span-2 xl:col-span-2 grid grid-cols-3 gap-2.5">
              <form.Field name="length">
                {(field) => {
                  const hasLengthErr = Boolean(field.state.meta.errors.length) || showLengthError || Boolean(serverFieldErrors.length);
                  return (
                  <div className="space-y-1" data-field="length">
                    <Label
                      htmlFor="length"
                      className={cn(
                        "text-xs font-semibold text-slate-700 flex items-center gap-1",
                        hasLengthErr && "text-destructive font-bold"
                      )}
                    >
                      Dài (mm) <span className="text-rose-500 font-bold">*</span>
                    </Label>
                    <NumericInput
                      decimals={1}
                      id="length"
                      name="length"
                      aria-invalid={hasLengthErr ? true : undefined}
                      className={cn(
                        "h-9 text-sm",
                        hasLengthErr && "border-destructive bg-red-50/40 ring-2 ring-destructive/25 font-semibold focus:ring-rose-500"
                      )}
                      placeholder="148"
                      value={field.state.value ?? ""}
                      onChange={(val) => {
                        if (Number(val) > 0) setShowLengthError(false);
                        field.handleChange(val);
                      }}
                    />
                    {hasLengthErr && (
                      <p className="text-[11px] text-destructive font-semibold" role="alert">
                        {String(field.state.meta.errors[0]?.message || field.state.meta.errors[0] || serverFieldErrors.length || "Vui lòng nhập chiều dài > 0")}
                      </p>
                    )}
                    {isSuspiciouslySmallDimension(field.state.value) && (
                      <div className="flex items-center justify-between text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-0.5">
                        <span>&lt; 10mm (cm?)</span>
                        <button
                          type="button"
                          onClick={() => field.handleChange(Math.round(Number(field.state.value) * 100) / 10)}
                          className="ml-1 text-blue-600 font-semibold underline hover:text-blue-800"
                        >
                          Đổi {Math.round(Number(field.state.value) * 100) / 10}mm
                        </button>
                      </div>
                    )}
                  </div>
                  );
                }}
              </form.Field>
              <form.Field name="width">
                {(field) => {
                  const hasWidthErr = Boolean(field.state.meta.errors.length) || showWidthError || Boolean(serverFieldErrors.width);
                  return (
                  <div className="space-y-1" data-field="width">
                    <Label
                      htmlFor="width"
                      className={cn(
                        "text-xs font-semibold text-slate-700 flex items-center gap-1",
                        hasWidthErr && "text-destructive font-bold"
                      )}
                    >
                      Rộng (mm) <span className="text-rose-500 font-bold">*</span>
                    </Label>
                    <NumericInput
                      decimals={1}
                      id="width"
                      name="width"
                      aria-invalid={hasWidthErr ? true : undefined}
                      className={cn(
                        "h-9 text-sm",
                        hasWidthErr && "border-destructive bg-red-50/40 ring-2 ring-destructive/25 font-semibold focus:ring-rose-500"
                      )}
                      placeholder="210"
                      value={field.state.value ?? ""}
                      onChange={(val) => {
                        if (Number(val) > 0) setShowWidthError(false);
                        field.handleChange(val);
                      }}
                    />
                    {hasWidthErr && (
                      <p className="text-[11px] text-destructive font-semibold" role="alert">
                        {String(field.state.meta.errors[0]?.message || field.state.meta.errors[0] || serverFieldErrors.width || "Vui lòng nhập chiều rộng > 0")}
                      </p>
                    )}
                    {isSuspiciouslySmallDimension(field.state.value) && (
                      <div className="flex items-center justify-between text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-0.5">
                        <span>&lt; 10mm (cm?)</span>
                        <button
                          type="button"
                          onClick={() => field.handleChange(Math.round(Number(field.state.value) * 100) / 10)}
                          className="ml-1 text-blue-600 font-semibold underline hover:text-blue-800"
                        >
                          Đổi {Math.round(Number(field.state.value) * 100) / 10}mm
                        </button>
                      </div>
                    )}
                  </div>
                  );
                }}
              </form.Field>
              <form.Field name="height">
                {(field) => (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-600">
                      Cao (mm)
                    </Label>
                    <NumericInput
                      decimals={1}
                      className="h-9 text-sm"
                      placeholder="50"
                      value={field.state.value ?? ""}
                      onChange={(val) => field.handleChange(val)}
                    />
                    {isSuspiciouslySmallDimension(field.state.value) && (
                      <div className="flex items-center justify-between text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-0.5">
                        <span>&lt; 10mm (cm?)</span>
                        <button
                          type="button"
                          onClick={() => field.handleChange(Math.round(Number(field.state.value) * 100) / 10)}
                          className="ml-1 text-blue-600 font-semibold underline hover:text-blue-800"
                        >
                          Đổi {Math.round(Number(field.state.value) * 100) / 10}mm
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </form.Field>
            </div>
            
            <div className="col-span-2 sm:col-span-2 lg:col-span-2 xl:col-span-2 grid grid-cols-2 gap-2.5">
              <form.Field name="flatLength">
                {(field) => (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-600">
                      Khổ trải dài (mm)
                    </Label>
                    <NumericInput
                      decimals={1}
                      className="h-9 text-sm"
                      placeholder="VD: 300"
                      value={field.state.value ?? ""}
                      onChange={(val) => field.handleChange(val)}
                    />
                  </div>
                )}
              </form.Field>
              <form.Field name="flatWidth">
                {(field) => (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-600">
                      Khổ trải rộng (mm)
                    </Label>
                    <NumericInput
                      decimals={1}
                      className="h-9 text-sm"
                      placeholder="VD: 400"
                      value={field.state.value ?? ""}
                      onChange={(val) => field.handleChange(val)}
                    />
                  </div>
                )}
              </form.Field>
            </div>

            {/* R4: Khổ giấy in, Khổ giấy mua, Số bát, Đóng gói - 4 cột độc lập */}
            <div className="col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1 space-y-1">
              <form.Field name="printFormat">
                {(field) => (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-600">
                      Khổ giấy in (mm)
                    </Label>
                    <PaperSizeInput
                      value={field.state.value}
                      onChange={(v) => field.handleChange(v)}
                      placeholderW="710"
                      placeholderH="450"
                      testId="paper-size-printFormat"
                    />
                  </div>
                )}
              </form.Field>
            </div>
            <div className="col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1 space-y-1">
              <form.Field name="cutFormat">
                {(field) => (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-600">
                      Khổ giấy mua (mm)
                    </Label>
                    <PaperSizeInput
                      value={field.state.value}
                      onChange={(v) => field.handleChange(v)}
                      placeholderW="770"
                      placeholderH="1000"
                      testId="paper-size-cutFormat"
                    />
                  </div>
                )}
              </form.Field>
            </div>
            <div className="col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1 space-y-1">
              <form.Field name="outs">
                {(field) => (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-600">
                      Số bát (Outs)
                    </Label>
                    <NumericInput
                      className="h-9 text-sm"
                      placeholder="VD: 6"
                      value={field.state.value ?? ""}
                      onChange={(val) => field.handleChange(val)}
                    />
                  </div>
                )}
              </form.Field>
            </div>
            <div className="col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1">
              <form.Field name="packingSpec">
                {(field) => (
                  <div className="space-y-1">
                    <Label className="text-xs font-medium text-slate-600">
                      Quy cách đóng gói
                    </Label>
                    <EditableCombobox
                      value={field.state.value || ""}
                      onChange={(val) => field.handleChange(val)}
                      suggestions={packingSpecSuggestions}
                      placeholder="VD: 100 cái/thùng"
                      className="h-9 text-sm"
                    />
                  </div>
                )}
              </form.Field>
            </div>
            <div className="col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1">
              <form.Field name="pcsPerBox">
                {(field) => (
                  <div className="space-y-1">
                    <Label htmlFor="product-pcs-per-box" className="text-xs font-medium text-slate-600">
                      Số cái / thùng
                    </Label>
                    <NumericInput
                      id="product-pcs-per-box"
                      value={(field.state.value as any) ?? ""}
                      onChange={(val) => {
                        const n =
                          val !== null && val !== undefined && (val as any) !== ""
                            ? Number(val)
                            : NaN;
                        field.handleChange(
                          (Number.isFinite(n) && n > 0 ? Math.round(n) : null) as any,
                        );
                      }}
                      placeholder="VD: 100"
                      className="h-9 text-sm"
                    />
                  </div>
                )}
              </form.Field>
            </div>

            {/* Ghi chú sản phẩm — lưu trên dữ liệu (không phải bình luận) */}
            <div className="col-span-full">
              <form.Field name={"notes" as any}>
                {(field: any) => (
                  <div className="space-y-1">
                    <Label htmlFor="product-notes" className="text-xs font-medium text-slate-600">
                      Ghi chú
                    </Label>
                    <Textarea
                      id="product-notes"
                      data-testid="product-notes"
                      value={field.state.value || ""}
                      onChange={(e) => field.handleChange(e.target.value)}
                      placeholder="Lưu ý của sản phẩm: yêu cầu khách, lưu ý sản xuất, đóng gói…"
                      className="min-h-[60px] text-sm"
                    />
                  </div>
                )}
              </form.Field>
            </div>

            {/* R5: Thông số Kỹ thuật Bao bì Offset chuyên sâu (Hướng thớ giấy, Khóa chiều xoay & Chứng nhận FSC) */}
            <form.Field name="specs">
              {(specsField) => {
                let parsedSpecs: any = {};
                try {
                  const raw = specsField.state.value;
                  if (typeof raw === "object" && raw !== null) parsedSpecs = { ...raw };
                  else if (typeof raw === "string" && raw.trim().startsWith("{")) {
                    parsedSpecs = JSON.parse(raw);
                  }
                } catch {}

                const grainVal = parsedSpecs.grainDirection || "ANY";
                const strictGrainVal = Boolean(parsedSpecs.strictGrainDirection);
                const fscVal = parsedSpecs.fscCode || "NONE";

                const updateSpecProp = (patch: Record<string, any>) => {
                  const next = { ...parsedSpecs, ...patch };
                  specsField.handleChange(JSON.stringify(next));
                };

                return (
                  <>
                    <div className="col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1 space-y-1">
                      <Label className="text-xs font-medium text-slate-600">
                        Hướng thớ giấy (Grain)
                      </Label>
                      <select
                        id="grainDirection"
                        data-testid="product-grain-direction-select"
                        value={grainVal}
                        onChange={(e) => {
                          const nextGrain = e.target.value;
                          updateSpecProp({
                            grainDirection: nextGrain,
                            strictGrainDirection:
                              nextGrain === "ANY" ? false : strictGrainVal,
                          });
                        }}
                        className="h-9 w-full rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      >
                        {GRAIN_DIRECTION_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-1 sm:col-span-1 lg:col-span-1 xl:col-span-1 flex flex-col justify-end pb-1">
                      <label
                        htmlFor="strictGrainDirection"
                        className={cn(
                          "flex items-center gap-2 rounded-md border px-2.5 py-2 text-xs cursor-pointer select-none transition-colors",
                          strictGrainVal
                            ? "border-amber-300 bg-amber-50 text-amber-900 font-semibold"
                            : "border-slate-200 bg-slate-50/60 text-slate-600"
                        )}
                      >
                        <Checkbox
                          id="strictGrainDirection"
                          data-testid="product-strict-grain-checkbox"
                          checked={strictGrainVal}
                          disabled={grainVal === "ANY"}
                          onCheckedChange={(checked) =>
                            updateSpecProp({ strictGrainDirection: Boolean(checked) })
                          }
                        />
                        <span className="leading-tight">
                          🔒 Khóa xoay thớ giấy (Chống nứt gân)
                        </span>
                      </label>
                    </div>

                    <div className="col-span-2 sm:col-span-2 lg:col-span-2 xl:col-span-2 space-y-1">
                      <Label className="text-xs font-medium text-slate-600">
                        Chứng nhận nguồn gốc rừng (FSC CoC)
                      </Label>
                      <select
                        id="fscCode"
                        data-testid="product-fsc-code-select"
                        value={fscVal}
                        onChange={(e) =>
                          updateSpecProp({
                            fscCode: e.target.value === "NONE" ? "" : e.target.value,
                          })
                        }
                        className="h-9 w-full rounded-md border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
                      >
                        {FSC_CERTIFICATION_OPTIONS.map((opt) => (
                          <option key={opt.id} value={opt.id}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                );
              }}
            </form.Field>
          </div>


          {/* IMAGE UPLOAD */}
          <div className="space-y-1">
            <Label className="text-xs font-medium text-slate-600 mb-1 block">
              Hình ảnh
            </Label>
            <form.Field name="image">
              {(field) => (
                <ImageDropzone
                  value={field.state.value as string}
                  onChange={(url) => field.handleChange(url)}
                  maxSizeKB={2048}
                  className="h-[200px] lg:h-[235px]"
                />
              )}
            </form.Field>
          </div>
        </div>
      </div>

      {/* ═══ SECTION 1.5: Thành phần con (Nếu là Combo) ═══ */}
      {isComposite && (
        <div className="border rounded-lg bg-white shadow-sm p-6 border-emerald-200 bg-emerald-50/30">
          <h3 className="text-sm font-bold text-emerald-700 mb-4">
            Các thành phần của Bộ sản phẩm
          </h3>
          <form.Field name="components">
            {(field) => (
              <ProductComponentsList
                components={(field.state.value as any) || []}
                onChange={(val) => field.handleChange(val as any)}
                materialLookup={materialLookup}
                processLookup={processLookup}
              />
            )}
          </form.Field>
        </div>
      )}

      {!isComposite && (
        <>
          {/* ═══ SECTION 2: Chất liệu & Quy trình ═══ */}
          <div className="border rounded-lg bg-white shadow-sm p-6">
            <h3 className="text-sm font-bold text-emerald-700 mb-4">
              Chất liệu & Quy trình
            </h3>

            {/* Row 1: Chất liệu */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 items-start">
              <form.Field name="specs">
                {(field) => {
                  let parsed: any = {};
                  try {
                    const raw = field.state.value;
                    if (typeof raw === "object" && raw !== null) parsed = raw;
                    else if (
                      typeof raw === "string" &&
                      raw.trim().startsWith("{")
                    )
                      parsed = JSON.parse(raw);
                  } catch {}

                  const typeIds: string[] = Array.isArray(
                    parsed.materialTypeIds,
                  )
                    ? parsed.materialTypeIds
                    : [];

                  const handleAdd = (id: string) => {
                    if (typeIds.includes(id)) return;
                    const nextIds = [...typeIds, id];
                    const nextSpecsStr = JSON.stringify({
                      ...parsed,
                      materialTypeIds: nextIds,
                    });
                    field.handleChange(nextSpecsStr);
                    setShowMaterialError(false);
                    form.setFieldValue(
                      "process",
                      buildDescription(
                        undefined,
                        undefined,
                        nextSpecsStr,
                      ) as any,
                    );
                  };
                  const handleRemove = (id: string) => {
                    const nextIds = typeIds.filter((v: string) => v !== id);
                    const nextSpecsStr = JSON.stringify({
                      ...parsed,
                      materialTypeIds: nextIds,
                    });
                    field.handleChange(nextSpecsStr);
                    form.setFieldValue(
                      "process",
                      buildDescription(
                        undefined,
                        undefined,
                        nextSpecsStr,
                      ) as any,
                    );

                    // Auto-Purge child materials if no longer matching
                    const currentMatIds = form.state.values.materialIds || [];
                    if (currentMatIds.length > 0) {
                      const purgedIds: string[] = [];
                      const purgedNames: string[] = [];
                      const keptMatIds: string[] = [];

                      for (const matId of currentMatIds) {
                        const opt = materialLookup.options.find((o) => o.id === matId);
                        const matTypeId = opt?.original?.typeId;
                        const isMismatched = nextIds.length > 0
                          ? (!matTypeId || !nextIds.includes(matTypeId))
                          : (matTypeId === id);

                        if (isMismatched) {
                          purgedIds.push(matId);
                          purgedNames.push(opt?.label || matId);
                        } else {
                          keptMatIds.push(matId);
                        }
                      }

                      if (purgedIds.length > 0) {
                        form.setFieldValue("materialIds", keptMatIds);
                        form.setFieldValue("process", buildDescription(keptMatIds));
                        const nextMats = (form.state.values.productMaterials || []).filter(
                          (m: any) => !purgedIds.includes(m.materialId)
                        );
                        form.setFieldValue("productMaterials", nextMats);
                        updatePaperSizesFromMaterialList(keptMatIds);
                        toast.warning(
                          `Đã tự động loại bỏ ${purgedNames.length} vật tư không còn thuộc nhóm loại vật tư đã chọn: ${purgedNames.join(", ")}`
                        );
                      }
                    }
                  };

                  const filteredOptions = materialTypeLookup.options.filter(
                    (o) => !typeIds.includes(o.id),
                  );

                  return (
                    <div className="space-y-1.5" id="materialTypeContainer">
                      <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                        Loại vật tư (Mặc định báo giá) <span className="text-rose-500 font-bold">*</span>
                      </Label>
                      <div
                        className={cn(
                          "min-h-[36px] w-full rounded-md border bg-white p-1 flex items-center flex-wrap gap-1 focus-within:ring-1 transition-shadow border-slate-200 focus-within:border-amber-500 focus-within:ring-amber-500",
                          showMaterialError && typeIds.length === 0 && "border-rose-500 ring-2 ring-rose-500 ring-offset-1"
                        )}
                      >
                        {typeIds.map((id: string) => {
                          const opt = materialTypeLookup.options.find(
                            (o) => o.id === id,
                          );
                          return (
                            <Badge
                              key={id}
                              variant="secondary"
                              className="text-[11px] h-6 px-2 py-0 gap-1 bg-amber-50 text-amber-700 hover:bg-amber-100 font-normal border-amber-200"
                            >
                              {opt ? opt.label : id}
                              <button
                                type="button"
                                className="ml-1 hover:bg-amber-200 rounded-full p-0.5 transition-colors"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemove(id);
                                }}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </Badge>
                          );
                        })}
                        <div className="flex-1 min-w-[150px]">
                          <ComboboxFK
                            value=""
                            onChange={(id) => id && handleAdd(id)}
                            options={filteredOptions}
                            loading={materialTypeLookup.isLoading}
                            label=""
                            hideLabel
                            placeholder={
                              typeIds.length === 0 ? "Chọn loại..." : ""
                            }
                            quickAddLabel="Thêm loại mới"
                            canAdd={hasPermission("products", "create")}
                            onQuickAddClick={(name) =>
                              setMaterialTypeModal({ open: true, name })
                            }
                          />
                        </div>
                      </div>
                      {showMaterialError && typeIds.length === 0 && (
                        <p className="text-[11px] text-rose-500 font-medium">
                          Vui lòng chọn ít nhất 1 loại vật tư
                        </p>
                      )}
                    </div>
                  );
                }}
              </form.Field>

              <form.Field name="materialIds">
                {(field) => {
                  const value = (field.state.value as string[]) || [];

                  let p: any = {};
                  try {
                    if (typeof currentSpecs === "object") p = currentSpecs;
                    else if (
                      typeof currentSpecs === "string" &&
                      currentSpecs.startsWith("{")
                    )
                      p = JSON.parse(currentSpecs);
                  } catch {}
                  const typeIdsFilter = Array.isArray(p.materialTypeIds)
                    ? p.materialTypeIds
                    : [];

                  return (
                    <TanstackFieldWrapper field={field} className="h-full">
                      <div className="space-y-1 h-full">
                        <Label className="text-xs font-medium text-slate-600">
                          Vật tư cấu thành
                        </Label>
                        <div
                          className={cn(
                            "min-h-[36px] w-full rounded-md border bg-white p-1 flex items-center flex-wrap gap-1 focus-within:ring-1 transition-shadow",
                            field.state.meta.errors.length > 0
                              ? "border-destructive focus-within:border-destructive focus-within:ring-destructive"
                              : "border-slate-200 focus-within:border-emerald-500 focus-within:ring-emerald-500",
                          )}
                        >
                          {value.map((id: string) => {
                            const opt = materialLookup.options.find(
                              (o) => o.id === id,
                            );
                            return (
                              <Badge
                                key={id}
                                variant="secondary"
                                className="text-[11px] h-6 px-2 py-0 gap-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 font-normal border-emerald-200"
                              >
                                {opt ? opt.label : id}
                                <button
                                  type="button"
                                  className="ml-1 hover:bg-emerald-200 rounded-full p-0.5 transition-colors"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const next = value.filter((v) => v !== id);
                                    field.handleChange(next);
                                    form.setFieldValue(
                                      "process",
                                      buildDescription(next),
                                    );
                                    const nextMats = (
                                      form.state.values.productMaterials || []
                                    ).filter((m) => m.materialId !== id);
                                    const autoBound =
                                      autoBindSingleMaterialToPrintProcess(
                                        nextMats,
                                        selectedProcessIds,
                                      );
                                    form.setFieldValue(
                                      "productMaterials",
                                      autoBound,
                                    );
                                    updatePaperSizesFromMaterialList(next);
                                  }}
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            );
                          })}
                          <div className="flex-1 min-w-[150px]">
                            <ComboboxFK {...processQuickAdd}
                              value=""
                              onChange={(id) => {
                                if (id && !value.includes(id)) {
                                  const next = [...value, id];
                                  field.handleChange(next);
                                  form.setFieldValue(
                                    "process",
                                    buildDescription(next),
                                  );

                                  // Sync productMaterials
                                  const currentProductMaterials =
                                    form.state.values.productMaterials || [];
                                  const newProductMaterials = next.map(
                                    (matId) => {
                                      const existing =
                                        currentProductMaterials.find(
                                          (m) => m.materialId === matId,
                                        );
                                      if (existing) return existing;
                                      return {
                                        materialId: matId,
                                        processId: "",
                                        standardQuantity: 0,
                                        note: "",
                                      };
                                    },
                                  );
                                  const autoBound =
                                    autoBindSingleMaterialToPrintProcess(
                                      newProductMaterials,
                                      selectedProcessIds,
                                    );
                                  form.setFieldValue(
                                    "productMaterials",
                                    autoBound,
                                  );

                                  updatePaperSizesFromMaterialList(next);
                                }
                              }}
                              options={materialLookup.options.filter(
                                (o) =>
                                  !value.includes(o.id) &&
                                  (typeIdsFilter.length === 0 ||
                                    (o.original?.typeId &&
                                      typeIdsFilter.includes(o.original?.typeId))),
                              )}
                              loading={materialLookup.isLoading}
                              label=""
                              hideLabel
                              placeholder={
                                value.length === 0 ? "Chọn VT..." : ""
                              }
                              quickAddLabel="Thêm vật tư"
                              canAdd={hasPermission("materials", "create")}
                              onQuickAddClick={(name) =>
                                setMaterialModal({ open: true, name, typeId: typeIdsFilter?.[0] })
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </TanstackFieldWrapper>
                  );
                }}
              </form.Field>

              
            </div>

            {/* Row 2: Các bước quy trình */}
            <div className="space-y-1.5 border-t border-slate-100 pt-4 mb-4">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                Quy trình sản xuất <span className="text-rose-500 font-bold">*</span>
              </Label>
              <ProcessRoutingPicker
                options={processLookup.options}
                selectedIds={selectedProcessIds}
                onChange={applyProcessIds}
                groupOf={getProcessGroupKey}
                onNormalize={handleNormalizeProcessOrder}
                isLoading={processLookup.isLoading}
                canAdd={hasPermission("processes", "create")}
                onQuickAdd={(name, groupKey) => setProcessModal({ open: true, name, groupKey })}
                hasError={(showProcessError && selectedProcessIds.length === 0) || showInProcessError || showThanhPhamProcessError}
                errorMessages={[
                  ...(showProcessError && selectedProcessIds.length === 0 ? ["Vui lòng chọn ít nhất 1 quy trình sản xuất"] : []),
                  ...(showInProcessError ? ["Bắt buộc chọn ít nhất 1 công đoạn In ấn"] : []),
                  ...(showThanhPhamProcessError ? ["Bắt buộc chọn ít nhất 1 công đoạn Thành phẩm"] : []),
                ]}
              />
            </div>

            {/* Cấu hình chi tiết công đoạn & chi phí (Kỹ thuật/Tính giá) - Collapsible default closed */}
            <Collapsible
              open={isProcessConfigsOpen}
              onOpenChange={setIsProcessConfigsOpen}
              className="mt-4 border rounded-lg bg-slate-50/50 p-3"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Settings className="w-4 h-4 text-slate-500" />
                  <span className="text-xs font-semibold text-slate-700">
                    Cấu hình định mức công đoạn & chi phí gia công (Kỹ thuật / Tính giá)
                  </span>
                  {form.state.values.processConfigs && form.state.values.processConfigs.length > 0 && (
                    <Badge variant="outline" className="text-[10px] bg-white">
                      {form.state.values.processConfigs.length} công đoạn
                    </Badge>
                  )}
                </div>
                <CollapsibleTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-slate-600 hover:text-slate-900 gap-1"
                  >
                    {isProcessConfigsOpen ? (
                      <>
                        Thu gọn <ChevronUp className="w-3.5 h-3.5" />
                      </>
                    ) : (
                      <>
                        ⚙️ Mở rộng cấu hình <ChevronDown className="w-3.5 h-3.5" />
                      </>
                    )}
                  </Button>
                </CollapsibleTrigger>
              </div>
              <CollapsibleContent className="pt-2">
                <form.Field name="processConfigs">
                  {(field) => (
                    // Theo dõi cả productMaterials: chọn Vật tư đầu vào chỉ đổi productMaterials, bảng phải vẽ lại
                    // để ô giữ vật tư vừa chọn (trước đây ô nhảy về "Chưa gắn vật tư" — FB-8D2LZP)
                    <form.Field name="productMaterials">
                      {(materialsField) => (
                        <ProductProcessConfigsList
                          configs={(field.state.value as any) || []}
                          onChange={(val) => field.handleChange(val as any)}
                          processLookup={processLookup}
                          productMaterials={(materialsField.state.value as any) || []}
                          materialLookup={materialLookup}
                          onProductMaterialsChange={(mats) =>
                            materialsField.handleChange(mats as any)
                          }
                          onMove={handleMoveProcess}
                          onNormalizeOrder={handleNormalizeProcessOrder}
                        />
                      )}
                    </form.Field>
                  )}
                </form.Field>
              </CollapsibleContent>
            </Collapsible>

            {/* Mô tả chi tiết — auto-generated */}
            <div className="border-t pt-4">
              <form.Field name="process">
                {(field) => (
                  <TanstackFieldWrapper
                    field={field}
                    label="Quy trình thực hiện"
                  >
                    <TanstackFormControl>
                      <Input
                        value={(field.state.value as any) || ""}
                        onChange={(e) =>
                          field.handleChange(e.target.value as any)
                        }
                        placeholder="Tự động tạo dựa trên lựa chọn ở trên..."
                      />
                    </TanstackFormControl>
                  </TanstackFieldWrapper>
                )}
              </form.Field>
            </div>
          </div>
        </>
      )}
      <div className="sticky bottom-0 z-50 -mx-4 sm:-mx-6 -mb-4 sm:-mb-6 backdrop-blur-md bg-white/80 p-4 border-t flex items-center justify-end gap-3 mt-6">
        <Button type="button" variant="outline" onClick={onCancel}>
          Huỷ
        </Button>
        <form.Subscribe selector={(s) => [s.isSubmitting]}>
          {([submitting]) => (
            <Button
              type="submit"
              disabled={submitting || isSubmitting}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {submitting || isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Đang lưu...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" /> {submitLabel}
                </>
              )}
            </Button>
          )}
        </form.Subscribe>
      </div>

      <CustomerFormModal
        open={customerModal.open}
        onClose={() => setCustomerModal({ open: false, name: "" })}
        initialData={{ name: customerModal.name }}
        breadcrumb={[...baseBreadcrumb, "Khách hàng"]}
        onSuccess={(item) => {
          form.setFieldValue("customerId", item.id);
          form.setFieldValue("customerName", item.label);
          setShowCustomerError(false);
          if (!isCodeManual) {
            trpcClient.products.generateCode
              .query({ customerId: item.id })
              .then((result) => {
                if (result?.code && !isCodeManual) {
                  form.setFieldValue("productCode", result.code);
                  setShowCodeError(false);
                }
              })
              .catch(() => {});
          }
        }}
      />

      <MaterialFormModal
        open={materialModal.open}
        onClose={() => setMaterialModal({ open: false, name: "", typeId: undefined })}
        initialData={{ name: materialModal.name, typeId: materialModal.typeId }}
        breadcrumb={[...baseBreadcrumb, "Vật tư"]}
        onSuccess={(item) => {
          queryClient.invalidateQueries({ queryKey: ["materials-lookup"] });
          queryClient.invalidateQueries({ queryKey: ["materials"] });
          const curVal = (form.getFieldValue("materialIds") as string[]) || [];
          if (!curVal.includes(item.id)) {
            const next = [...curVal, item.id];
            form.setFieldValue("materialIds", next);
            form.setFieldValue("process", buildDescription(undefined, next));
            const currentProductMaterials = form.state.values.productMaterials || [];
            const newProductMaterials = next.map((matId) => {
              const existing = currentProductMaterials.find((m: any) => m.materialId === matId);
              if (existing) return existing;
              return {
                materialId: matId,
                processId: "",
                standardQuantity: 0,
                note: "",
              };
            });
            const autoBound = autoBindSingleMaterialToPrintProcess(
              newProductMaterials,
              selectedProcessIds,
            );
            form.setFieldValue("productMaterials", autoBound);
            updatePaperSizesFromMaterialList(next);
          }
        }}
      />

      <MaterialTypeFormModal
        open={materialTypeModal.open}
        onClose={() =>
          setMaterialTypeModal((prev) => ({ ...prev, open: false }))
        }
        initialData={{ code: materialTypeModal.name, label: materialTypeModal.name }}
        onSuccess={(newItem) => {
          const id = newItem.id;
          // Add the new material type to the specs.materialTypeIds
          const rawSpecs = form.state.values.specs as string;
          let parsed: any = {};
          try {
            if (typeof rawSpecs === "string" && rawSpecs.startsWith("{"))
              parsed = JSON.parse(rawSpecs);
            else if (typeof rawSpecs === "object" && rawSpecs)
              parsed = rawSpecs;
          } catch {
            /* */
          }
          const typeIds = Array.isArray(parsed.materialTypeIds)
            ? [...parsed.materialTypeIds]
            : [];
          if (!typeIds.includes(id)) typeIds.push(id);
          parsed.materialTypeIds = typeIds;
          form.setFieldValue("specs", JSON.stringify(parsed));
          form.setFieldValue(
            "process",
            buildDescription(undefined, undefined, JSON.stringify(parsed)),
          );
        }}
      />


      <ProcessCreateModal
        open={processModal.open}
        onClose={() => setProcessModal((prev) => ({ ...prev, open: false }))}
        initialName={processModal.name}
        initialGroupKey={processModal.groupKey}
        onCreated={(id) => {
          if (id && !selectedProcessIds.includes(id)) {
            const next = [...selectedProcessIds, id];
            setSelectedProcessIds(next);
            form.setFieldValue("processIds", next);
            setShowProcessError(false);
            form.setFieldValue(
              "process",
              buildDescription(undefined, next) as any,
            );

            const currentConfigs = form.state.values.processConfigs || [];
            form.setFieldValue("processConfigs", [
              ...currentConfigs,
              {
                processId: id,
                setupTimeMinutes: 0,
                speedPerHour: 0,
                runWastePercent: 0,
                setupWasteQty: 0,
                isOutsourced: false,
                unitCost: 0,
                batchCost: 0,
              },
            ]);
          }
        }}
      />
    </form>
  );
}
